import { useEffect, useState } from 'react';
import { Col, FormControl, Row, Spinner, Stack } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { Forbidden } from '../../../../../components/error/Forbidden';
import { useToast } from '../../../../../context';
import { useGetTerritoryQuery } from '../../../../../services/endpoints/places/territory';
import { getCurrentUser } from '../../../../../services/helper';
import { SFCTerritory } from '../../../../../types/place.types';
import { formatDate } from '../../../../../utils/dateUtils';
import { canEditTerritory, canViewTerritories, canViewTerritoryReports } from '../../../../../utils/rbac';

// =============================================================================
// COMPONENT
// =============================================================================

export const TerritoryDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const currentUser = getCurrentUser();
  const userHasAccess =  canViewTerritories(currentUser);

  const { data, error, isLoading } = useGetTerritoryQuery(+params.id!, {
    skip: !userHasAccess
  });
  const [territory, setTerritory] = useState<SFCTerritory>();
  const [zipsString, setZipsString] = useState<string>('');

  // Handle API errors with better messaging
  useEffect(() => {
    if (error && !isLoading) {
      let errorMessage = 'Error while loading territory';

      // Extract error message from API response
      if ('data' in error && typeof error.data === 'object' && error.data !== null) {
        const apiError = error.data as { message?: string };
        if (apiError.message) {
          errorMessage = apiError.message;
        }
      } else if ('message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      addToast(errorMessage, 'error');
    }
  }, [error, isLoading, addToast]);

  useEffect(() => {
    if (data) {
      setTerritory(data.territory);
      setZipsString(
          data.territory.zip_codes
              .map((z) => z.code)
              .sort()
              .join(', ')
      );
    }
  }, [data]);

  // Check permissions before rendering
  if (!userHasAccess) {
    return <Forbidden />;
  }

  if (isLoading || !territory) {
    return (
        <div className="w-100 vh-100 d-inline-flex">
          <Spinner variant="primary" className="m-auto" />
        </div>
    );
  }

  return (
      <>
        <div
            className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
            style={{ background: '#EFEFEF' }}
        >
          <Stack direction="horizontal" gap={2} className="ps-lg-3 ps-0">
            <button
                className="btn btn-link text-info p-0"
                onClick={() => navigate('/admin/places/territories')}
            >
              <FaArrowLeft size={20} className="d-lg-none" />
              <FaArrowLeft size={24} className="d-none d-lg-block" />
            </button>
            <h2
                className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
                style={{ color: '#888888', fontSize: '1rem' }}
            >
              Territories List
            </h2>
            <h2 className="d-none d-lg-block text-info fw-bold mb-0">{`${territory?.title}`}</h2>
          </Stack>
          <Stack direction="horizontal" gap={1} className="d-none d-lg-flex">
            {canEditTerritory(currentUser) && (
                <SimpleButton
                    variant="primary"
                    height={36}
                    onClick={() => navigate(`/admin/places/territories/${territory?.id}/edit`)}
                >
                  Edit Territory
                </SimpleButton>
            )}
          </Stack>
        </div>
        <div>
          <Stack gap={4} className="p-4">
            <Row>
              <Col>
                <DetailSection title="Details">
                  <LabelText label="Title:" text={territory?.title} />
                  <LabelText label="Created At:">
                    {formatDate(new Date(territory?.created_at), 'MMM dd, yyyy hh:mm a')}
                  </LabelText>
                  <LabelText label="Location:">
                    <LinkCell
                        content={territory?.location?.title ?? 'No location title'}
                        url={`/admin/places/locations/${territory?.location?.id}`}
                    />
                  </LabelText>
                  <LabelText label="Zip Codes:">
                    <FormControl as="textarea" value={zipsString} disabled rows={10} cols={50} />
                  </LabelText>
                </DetailSection>
              </Col>
              <Col>
                {canViewTerritoryReports(currentUser, territory.location_id) && (
                    <DetailSection title="Reports">
                      <LinkCell
                          content={'Summary Session Financials'}
                          url={`/admin/places/territories/${territory.id}/ledger`}
                      />
                      {/* <LinkCell content={'Gross Revenue Report'} url={`/admin/places/territories/${territory.id}/weekly-report`} /> */}
                    </DetailSection>
                )}
              </Col>
            </Row>
          </Stack>
        </div>
      </>
  );
};